'use client';

import {
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import React from 'react';

import { useAddress } from '../../contexts/AddressContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import {
  UserAddress,
  createUserAddress,
  updateUserAddress,
  deleteUserAddress,
  setDefaultAddress,
} from '../../lib/api/addresses';
import { FeatureDisabled } from '../fallbacks/FeatureDisabled';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function HeaderAddressSelector() {
  // Rules of Hooks: ALL hooks must be called unconditionally at the top level.
  // Conditional returns must come AFTER all hook calls.

  // Safely capture address context — flag failure instead of returning early
  let addressContext: ReturnType<typeof useAddress> | null = null;
  let addressContextFailed = false;
  try {
    addressContext = useAddress();
  } catch {
    // No AddressProvider — will return null below after all hooks are called
    addressContextFailed = true;
  }

  // All hooks declared unconditionally (Rules of Hooks)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const addressFeatureEnabled = useFeatureFlag('address-management', true);
  const [formData, setFormData] = React.useState({
    label: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
  });

  // Conditional returns AFTER all hooks
  if (addressContextFailed || !addressContext) return null;

  const { selectedAddress, addresses, setSelectedAddress, refreshAddresses, error: contextError, showAddAddressDialog, setShowAddAddressDialog, editingAddress, setEditingAddress } = addressContext;

  // If address management is disabled or context has critical errors, hide silently
  if (!addressFeatureEnabled || contextError) {
    return null;
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddressSelect = async (address: UserAddress) => {
    try {
      if (setSelectedAddress) {
        setSelectedAddress(address);
      }
      handleMenuClose();
    } catch (error) {
      console.error('Failed to select address:', error);
      // Graceful degradation - still close menu but log error
      handleMenuClose();
      // Continue without showing error to user - address selection is not critical
    }
  };

  const handleAddAddress = () => {
    setFormData({
      label: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'US',
    });
    setEditingAddress(null);
    setShowAddAddressDialog(true);
    handleMenuClose();
  };

  const handleEditAddress = (address: UserAddress) => {
    setFormData({
      label: address.label || '',
      street_address: address.street_address,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      country: address.country,
    });
    setEditingAddress(address);
    setShowAddAddressDialog(true);
    handleMenuClose();
  };

  const handleDeleteAddress = async (address: UserAddress) => {
    if (!confirm(`Delete address "${address.label || address.street_address}"?`)) return;

    try {
      await deleteUserAddress(address.id);
      if (refreshAddresses) {
        await refreshAddresses();
      }

      // If we deleted the selected address, select another one
      if (selectedAddress?.id === address.id && setSelectedAddress) {
        const remaining = addresses.filter(a => a.id !== address.id);
        const newSelected = remaining.find(a => a.is_default) || remaining[0] || null;
        setSelectedAddress(newSelected);
      }
    } catch (error) {
      console.error('Failed to delete address:', error);
      // Graceful degradation - show user-friendly message but don't break the UI
      alert('Unable to delete address right now. Please try again later.');
    }
  };

  const handleFormSubmit = async () => {
    try {
      let createdAddress: UserAddress | undefined;

      if (editingAddress) {
        await updateUserAddress(editingAddress.id, formData);
      } else {
        // For new addresses, capture the created address
        createdAddress = await createUserAddress(formData);
      }

      if (refreshAddresses) {
        await refreshAddresses();
      }

      // Auto-select the newly created address
      if (createdAddress && setSelectedAddress) {
        setSelectedAddress(createdAddress);
      }

      setShowAddAddressDialog(false);
    } catch (error) {
      console.error('Failed to save address:', error);
      // Graceful degradation - keep dialog open but show error
      alert('Unable to save address right now. Please check your connection and try again.');
    }
  };

  const formatAddressDisplay = (address: UserAddress) => {
    const parts = [
      address.label,
      address.street_address,
      `${address.city}, ${address.state} ${address.zip_code}`
    ].filter(Boolean);
    return parts.join(' • ');
  };

  const displayText = selectedAddress
    ? formatAddressDisplay(selectedAddress)
    : addresses.length > 0
      ? 'Select service address'
      : 'Add service address';

  // Amazon-style header display
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocationIcon color="action" />
        <Box>
          <Typography variant="body2" color="text.secondary">
            Service Address
          </Typography>
          <Button
            onClick={handleMenuOpen}
            sx={{
              p: 0,
              textTransform: 'none',
              fontSize: 'inherit',
              fontWeight: 600,
              color: 'text.primary',
              '&:hover': { backgroundColor: 'transparent' }
            }}
            endIcon={<ExpandMoreIcon />}
          >
            {selectedAddress ? (
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body1" fontWeight={600}>
                  {selectedAddress.label || 'Address'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedAddress.street_address}, {selectedAddress.city}, {selectedAddress.state} {selectedAddress.zip_code}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body1" fontWeight={600}>
                {addresses.length > 0 ? 'Select service address' : 'Add service address'}
              </Typography>
            )}
          </Button>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 300 }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Choose your location</Typography>
        </Box>

        {addresses.map((address) => (
          <MenuItem
            key={address.id}
            onClick={() => handleAddressSelect(address)}
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              py: 2,
              borderBottom: address.id === selectedAddress?.id ? 2 : 1,
              borderColor: address.id === selectedAddress?.id ? 'primary.main' : 'divider'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={address.id === selectedAddress?.id ? 600 : 400}>
                  {address.label || 'Address'}
                  {address.is_default && <Chip label="Default" size="small" color="primary" sx={{ ml: 1 }} />}
                  {address.id === selectedAddress?.id && <Chip label="Current" size="small" color="success" sx={{ ml: 1 }} />}
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditAddress(address); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteAddress(address); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {address.street_address}<br />
              {address.city}, {address.state} {address.zip_code}
            </Typography>
          </MenuItem>
        ))}

        <MenuItem onClick={handleAddAddress} sx={{ py: 2 }}>
          <AddIcon sx={{ mr: 1 }} />
          <Typography>Add new address</Typography>
        </MenuItem>
      </Menu>

      {/* Address Dialog */}
      <Dialog open={showAddAddressDialog} onClose={() => setShowAddAddressDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAddress ? 'Edit Address' : 'Add New Address'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Label (optional)"
              value={formData.label}
              onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
              placeholder="Home, Office, etc."
              fullWidth
            />
            <TextField
              label="Street Address"
              value={formData.street_address}
              onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
              required
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="City"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                required
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  label="State"
                >
                  {US_STATES.map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="ZIP Code"
              value={formData.zip_code}
              onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
              required
              sx={{ maxWidth: 150 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddAddressDialog(false)}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained">
            {editingAddress ? 'Update' : 'Add'} Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}